import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { animate, stagger } from "animejs";
import {
  Code2,
  Layers,
  Cloud,
  Database,
  TestTubeDiagonal,
  Bot,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

gsap.registerPlugin(ScrollTrigger);

const SECTIONS = [
  {
    title: "Languages",
    icon: <Code2 className="h-5 w-5" />,
    color: "from-cyan-500/40 to-cyan-500/0",
    items: [
      { name: "JavaScript", logo: "/images/logo/javascript.svg", level: 95 },
      { name: "TypeScript", logo: "/images/logo/typescript.svg", level: 92 },
      { name: "Dart", logo: "/images/logo/dart.svg", level: 80 },
    ],
  },
  {
    title: "Frameworks",
    icon: <Layers className="h-5 w-5" />,
    color: "from-fuchsia-500/40 to-fuchsia-500/0",
    items: [
      { name: "React", logo: "/images/logo/react.svg", level: 95 },
      { name: "React Native", logo: "/images/logo/react.svg", level: 88 },
      { name: "Next.js", logo: "/images/logo/next.svg", level: 90 },
      { name: "Flutter", logo: "/images/logo/flutter.svg", level: 80 },
      { name: "Node.js", logo: "/images/logo/nodejs.svg", level: 90 },
      { name: "Nest.js", logo: "/images/logo/nestjs.svg", level: 85 },
    ],
  },
  {
    title: "Cloud Platforms",
    icon: <Cloud className="h-5 w-5" />,
    color: "from-violet-500/40 to-violet-500/0",
    items: [
      { name: "Cloudflare", logo: "/images/logo/cloudflare.svg", level: 85 },
      { name: "AWS", logo: "/images/logo/aws.svg", level: 80 },
      { name: "GCP", logo: "/images/logo/gcp.svg", level: 75 },
    ],
  },
  {
    title: "Databases",
    icon: <Database className="h-5 w-5" />,
    color: "from-emerald-500/40 to-emerald-500/0",
    items: [
      { name: "SQL", logo: "/images/logo/sql.svg", level: 85 },
      { name: "MongoDB", logo: "/images/logo/mongo.svg", level: 88 },
    ],
  },
  {
    title: "Testing",
    icon: <TestTubeDiagonal className="h-5 w-5" />,
    color: "from-amber-500/40 to-amber-500/0",
    items: [
      { name: "Jest", logo: "/images/logo/jest.svg", level: 88 },
      { name: "Enzyme", logo: "/images/logo/enzyme.svg", level: 75 },
    ],
  },
  {
    title: "Bots & Notifications",
    icon: <Bot className="h-5 w-5" />,
    color: "from-pink-500/40 to-pink-500/0",
    items: [
      { name: "LINE OA / Notify", logo: "/images/logo/line.svg", level: 85 },
      { name: "Discord Bot", logo: "/images/logo/discord.svg", level: 80 },
    ],
  },
];

const SkillTile = ({ item, index }) => {
  const tileRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    const tile = tileRef.current;
    const bar = barRef.current;
    if (!tile || !bar) return;

    const trigger = ScrollTrigger.create({
      trigger: tile,
      start: "top 90%",
      once: true,
      onEnter: () => {
        animate(tile, {
          opacity: [0, 1],
          translateY: [40, 0],
          scale: [0.95, 1],
          duration: 700,
          delay: index * 60,
          ease: "outExpo",
        });
        animate(bar, {
          width: ["0%", `${item.level}%`],
          duration: 1200,
          delay: 200 + index * 60,
          ease: "outExpo",
        });
      },
    });
    return () => trigger.kill();
  }, [item.level, index]);

  return (
    <div ref={tileRef} className="opacity-0">
      <Card className="group relative overflow-hidden p-4 transition-all hover:-translate-y-1 hover:shadow-[0_0_40px_-10px_rgba(34,211,238,0.6)]">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-cyan-500/10 opacity-0 blur-3xl transition-opacity group-hover:opacity-100" />
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-lg border border-white/10 bg-white/5 p-2">
            <img
              src={item.logo}
              alt={item.name}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">{item.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              proficiency
            </p>
          </div>
          <span className="font-mono text-sm font-bold text-cyan-300">
            {item.level}%
          </span>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            ref={barRef}
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-400 to-violet-400 shadow-[0_0_12px_rgba(34,211,238,0.6)]"
            style={{ width: 0 }}
          />
        </div>
      </Card>
    </div>
  );
};

const ExperienceIndex = () => {
  const titleRef = useRef(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    animate(el.querySelectorAll("[data-char]"), {
      opacity: [0, 1],
      translateY: [30, 0],
      duration: 700,
      delay: stagger(40),
      ease: "outExpo",
    });
  }, []);

  const title = "Tech Stack";

  return (
    <section className="mx-auto max-w-7xl px-6 pb-32 pt-28">
      <header className="mb-14 text-center">
        <Badge variant="neon" className="mb-4">
          {"// skills.json"}
        </Badge>
        <h2
          ref={titleRef}
          className="text-glow text-4xl font-bold sm:text-5xl lg:text-6xl"
        >
          {title.split("").map((c, i) => (
            <span
              key={i}
              data-char
              className="inline-block opacity-0"
              style={{ whiteSpace: c === " " ? "pre" : "normal" }}
            >
              {c}
            </span>
          ))}
        </h2>
        <p className="mt-4 text-base text-white/60">
          Tools, frameworks and platforms I work with daily.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {SECTIONS.map((section, sIdx) => (
          <Card
            key={section.title}
            className={`relative overflow-hidden p-6 ${
              sIdx % 2 === 0 ? "lg:translate-y-4" : ""
            }`}
          >
            <div
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${section.color} opacity-30`}
            />
            <div className="relative">
              <div className="mb-5 flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg border border-white/10 bg-white/5 text-cyan-300">
                  {section.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{section.title}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {section.items.length} items
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {section.items.map((item, i) => (
                  <SkillTile key={item.name} item={item} index={i} />
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default ExperienceIndex;
